import React from 'react';
import { camelize, camelizeKeys } from 'humps';
import { ICountry } from '../schemas/core-schema';

import RegionSelect from './addresses/region_select';
import {
  AddressInputField,
  LabelOverride,
  Field,
} from '../lib/address_form_fields_types';
import { buildInputName } from '../lib/address_form_fields_helpers';
import { IAddressFormData } from './addresses/address_form_data';

import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import FormGroup from '@reverbdotcom/commons/src/components/form_group';

interface Props {
  field: Field;
  fieldPrefix: string;
  user: Partial<IUser>;
  address: IAddressFormData;
  countries: ICountry[];
  updateField: (attributes) => void;
  requiredFieldsOverrides: AddressInputField[];
  labelOverrides: LabelOverride;
  phoneLabel: JSX.Element | React.ReactNode;
  handleUpdateField?: boolean;
}

function camelizedCountries(countries) {
  return countries.map(c => camelizeKeys(c)) as ICountry[];
}

function handleInputChange(event, address, updateField, handleUpdateField) {
  if (handleUpdateField) {
    updateField(camelizeKeys(event));
  } else {
    const name = Object.keys(event)[0];

    updateField({ ...address, [name]: event[name] });
  }
}

function onRegionChange(region, address, handleUpdateField, updateField) {
  if (handleUpdateField) {
    updateField({ region });
  } else {
    updateField({ ...address, region });
  }
}

function getFieldLabel({ fieldName, display }: Field, user: Partial<IUser>, labelOverrides, phoneLabel) {
  if (fieldName === 'phone' && !!phoneLabel) {
    return phoneLabel;
  }

  return buildLabel(fieldName, display[user.locale], labelOverrides);
}

function buildLabel(fieldName, fieldLabel, labelOverrides: LabelOverride) {
  return labelOverrides[fieldName] || fieldLabel;
}

export function AddressFormField(props: Props) {
  if (props.field.fieldName === 'region') {
    return (
      <FormGroup
        key={props.field.fieldName}
        label={props.field.display[props.user.locale]}
      >
        <RegionSelect
          name={buildInputName(props.fieldPrefix, 'region')}
          countries={camelizedCountries(props.countries)}
          value={props.address.region}
          countryCode={props.address.countryCode}
          onChange={(event) => onRegionChange(event.target.value, props.address, props.handleUpdateField, props.updateField)}
        />
      </FormGroup>
    );
  }

  return (
    <FormGroupWithInput
      key={props.field.fieldName}
      required={props.field.required || props.requiredFieldsOverrides.includes(props.field.fieldName)}
      label={getFieldLabel(props.field, props.user, props.labelOverrides, props.phoneLabel)}
      inputName={buildInputName(props.fieldPrefix, props.field.fieldName)}
      fieldName={props.field.fieldName}
      value={props.address[camelize(props.field.fieldName)]}
      updateField={event => handleInputChange(event, props.address, props.updateField, props.handleUpdateField)}
      tagOptional={!props.field.required && !props.requiredFieldsOverrides.includes(props.field.fieldName)}
    />
  );
}

export default AddressFormField;
